import React, { useState, useEffect, useMemo } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    Table,
    Button,
    FormGroup,
    Input,
    Label,
    Spinner,
    Pagination,
    Modal,
    ModalHeader,
    ModalBody,
} from 'reactstrap';
import { toast, ToastContainer } from 'react-toastify';
import axios from 'axios';
import ReactPaginate from 'react-paginate';
import { apiURL } from '../../config';
import { FaEye, FaArrowRight } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Footer from 'components/Footers/Footer';

const OvertimeRequests = () => {
    const token = localStorage.getItem("token");
    const navigate = useNavigate();

    const [overtimeData, setOvertimeData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterName, setFilterName] = useState('');
    const [filterDate, setFilterDate] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const itemsPerPage = 20;
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [approveModal, setApproveModal] = useState(false);
    const [rejectModal, setRejectModal] = useState(false);
    const [viewModal, setViewModal] = useState(false);
    const [totalCount, setTotalCount] = useState(0);

    const columns = ['Staff Name', 'Email', 'Date', 'Check-in', 'Check-out', 'Hours', 'Status', 'Actions'];

    const formatDate = (dateStr) => {
        const d = new Date(dateStr);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}-${month}-${year}`;
    };


    const fetchOvertimeData = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${apiURL}/superadmin/overtime/requests`, {
                headers: {
                    'Authorization': `Bearer ${token}`, // Include the token in the headers
                }
            });
            const data = response.data;
            // console.log('data is ', response.data)
            const formattedData = data.map(item => ({
                teacherId: item.teacherId,
                teacherName: item.teacherName,
                teacherEmail: item.teacherEmail,
                date: formatDate(item.date),
                checkIn: item.checkIn ? formatDate(item.checkIn) : '-',
                checkOut: item.checkOut ? formatDate(item.checkOut) : '-',
                hours: item.hours,
                requestDate: item.requestDate,
                notes: item.notes,
                attendanceId: item.attendanceId,
                status: item.status || 'pending',
            }));
            formattedData.sort((a, b) => new Date(b.requestDate) - new Date(a.requestDate));
            setOvertimeData(formattedData);
            setTotalCount(formattedData.length);
        } catch (error) {
            console.error('Error fetching overtime data:', error);
            toast.error('Error fetching overtime data!');
            setOvertimeData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOvertimeData();
    }, []);

    const handleFilterNameChange = (e) => {
        setFilterName(e.target.value);
    };

    const handleFilterDateChange = (e) => {
        setFilterDate(e.target.value);
    };

    const filteredOvertimeData = useMemo(() => {
        return overtimeData.filter((item) => {
            const nameMatch = filterName
                ? item.teacherName?.toLowerCase().includes(filterName.toLowerCase())
                : true;
            const dateMatch = filterDate
                ? new Date(item.date).toLocaleDateString() ===
                new Date(filterDate).toLocaleDateString()
                : true;
            return nameMatch && dateMatch;
        });
    }, [overtimeData, filterName, filterDate]);

    const handlePageChange = (selectedPage) => {
        setCurrentPage(selectedPage.selected);
    };

    const paginatedOvertimeData = useMemo(() => {
        const startIndex = currentPage * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return filteredOvertimeData.slice(startIndex, endIndex);
    }, [filteredOvertimeData, currentPage, itemsPerPage]);

    const handleApproveReject = async (status) => {
        if (!selectedRequest) return;
        // console.log('teacherId ', selectedRequest.teacherId, 'attendanceId: ', selectedRequest.attendanceId, ' status ', status)
        try {
            await axios.put(`${apiURL}/superadmin/overtime/requests/update`, {
                teacherId: selectedRequest.teacherId,
                attendanceId: selectedRequest.attendanceId,
                status: status,
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`, // Include the token in the headers
                }
            });
            toast.success(`Overtime request ${status} successfully!`);
            fetchOvertimeData();
        } catch (error) {
            console.error('Error updating overtime status:', error);
            toast.error('Error updating overtime status!');
        } finally {
            setApproveModal(false);
            setRejectModal(false);
        }
    };

    const approveToggle = () => setApproveModal(!approveModal);
    const rejectToggle = () => setRejectModal(!rejectModal);
    const viewToggle = () => setViewModal(!viewModal);

    return (
        <>
            <div
                className="header   d-flex align-items-center"
                style={{
                    minHeight: '600px',
                    backgroundImage:
                        'url(' + require('../../assets/img/theme/alRehman.jpg') + ')',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center top',
                }}
            >
                <span className="mask bg-gradient-default opacity-8" />
                <Container className="d-flex align-items-center" fluid>
                    <Row>
                        <Col lg="7" md="10">
                            <h1 className="display-2 text-white">Overtime Requests</h1>
                            <p className="text-white mt-0 mb-5">
                                View and manage staff overtime requests.
                            </p>
                        </Col>
                    </Row>
                </Container>
            </div>
            <Container className="mt--7" fluid>
                <Row>
                    <Col>
                        <Card className="shadow">
                            <CardHeader className="border-0 d-flex justify-content-between align-items-center">
                                <h3 className="mb-0">Overtime Requests</h3>
                            </CardHeader>
                            <CardBody>
                                <Row className="mb-3">
                                    <Col md="4">
                                        <FormGroup>
                                            <Label>Filter by Name</Label>
                                            <Input
                                                type="text"
                                                placeholder="Enter staff name"
                                                value={filterName}
                                                onChange={handleFilterNameChange}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="4">
                                        <FormGroup>
                                            <Label>Filter by Date</Label>
                                            <Input
                                                type="date"
                                                value={filterDate}
                                                onChange={handleFilterDateChange}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                {loading ? (
                                    <Spinner size="lg" className="my-5 mx-auto" />
                                ) : (
                                    <Table className="align-items-center table-flush" responsive>
                                        <thead className="thead-light">
                                            <tr>
                                                {columns.map((col, index) => (
                                                    <th key={index}>{col}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {paginatedOvertimeData && paginatedOvertimeData.length > 0 ? (
                                                paginatedOvertimeData.map((item) => (
                                                    <tr key={item.attendanceId}>
                                                        <td>{item.teacherName}</td>
                                                        <td>{item.teacherEmail}</td>
                                                        <td>{item.date}</td>
                                                        <td>{item.checkIn}</td>
                                                        <td>{item.checkOut}</td>
                                                        <td>{item.hours}</td>
                                                        <td>{item.status}</td>
                                                        <td>
                                                            <div className="d-flex">
                                                                <Button
                                                                    size="sm"
                                                                    color="info"
                                                                    onClick={() => {
                                                                        setSelectedRequest(item);
                                                                        setViewModal(true);
                                                                    }}
                                                                    style={{ marginRight: '5px' }}
                                                                >
                                                                    <FaEye />
                                                                </Button>
                                                                <Button
                                                                    size="sm"
                                                                    color="success"
                                                                    onClick={() => {
                                                                        setSelectedRequest(item);
                                                                        setApproveModal(true);
                                                                    }}
                                                                    style={{ marginRight: '5px' }}
                                                                >
                                                                    Approve
                                                                </Button>
                                                                <Button
                                                                    size="sm"
                                                                    color="danger"
                                                                    onClick={() => {
                                                                        setSelectedRequest(item);
                                                                        setRejectModal(true);
                                                                    }}
                                                                >
                                                                    Reject
                                                                </Button>
                                                                <Button
                                                                    size="sm"
                                                                    color="primary"
                                                                    onClick={() => navigate(`/admin/AddTeacher/${item.teacherId}`)}
                                                                >
                                                                    <FaArrowRight />
                                                                </Button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={columns.length}>No overtime requests found.</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                )}
                                <Pagination className="d-flex justify-content-center">
                                    <ReactPaginate
                                        previousLabel={
                                            <i
                                                className="previous fa fa-chevron-left"
                                                aria-hidden="true"
                                            ></i>
                                        }
                                        nextLabel={
                                            <i
                                                className="next fa fa-chevron-right"
                                                aria-hidden="true"
                                            ></i>
                                        }
                                        breakLabel={"..."}
                                        pageCount={Math.ceil(
                                            totalCount / itemsPerPage
                                        )}
                                        onPageChange={handlePageChange}
                                        containerClassName={"pagination justify-content-end mb-0"}
                                        pageClassName={"page-item"}
                                        pageLinkClassName={"page-link"}
                                        activeClassName={"active"}
                                        previousClassName={"page-item"}
                                        nextClassName={"page-item"}
                                        previousLinkClassName={"page-link"}
                                        nextLinkClassName={"page-link"}
                                    />
                                </Pagination>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
            {/* View Modal */}
            <Modal isOpen={viewModal} toggle={viewToggle} size="lg">
                <ModalHeader toggle={viewToggle}>Overtime Request Details</ModalHeader>
                <ModalBody style={{ overflowWrap: 'break-word' }}>
                    {selectedRequest && (
                        <>
                            <p><strong>Staff Name:</strong> {selectedRequest.teacherName}</p>
                            <p><strong>Email:</strong> {selectedRequest.teacherEmail}</p>
                            <p><strong>Date:</strong> {selectedRequest.date}</p>
                            <p><strong>Check-in Time:</strong> {selectedRequest.checkIn !== '-' ? selectedRequest.checkIn : '-'}</p>
                            <p><strong>Check-out Time:</strong> {selectedRequest.checkOut !== '-' ? selectedRequest.checkOut : '-'}</p>
                            <p><strong>Hours:</strong> {selectedRequest.hours}</p>
                            <p><strong>Notes:</strong> {selectedRequest.notes || 'No notes provided.'}</p>
                        </>
                    )}
                </ModalBody>
            </Modal>
            <Modal isOpen={approveModal} toggle={approveToggle}>
                <ModalHeader className="pb-0">Approve Overtime Request</ModalHeader>
                <ModalBody className="pb-0 d-flex flex-column justify-content-center align-items-center ">
                    <Row>
                        <i
                            className="fa fa-check-circle fa-3x fa-primary"
                            style={{
                                color: '#2dce89',
                            }}
                            aria-hidden="true"
                        ></i>
                    </Row>
                    <Row className="mt-4">
                        Are you sure about Approve Overtime Request?
                    </Row>
                    <Button
                        color="success"
                        onClick={() => handleApproveReject('approved')}
                        className="w-100 mt-4"
                    >
                        Yes, Approve
                    </Button>
                    <Button
                        color="secondary"
                        onClick={approveToggle}
                        className="w-100 mt-4 mb-4"
                    >
                        Cancel
                    </Button>
                </ModalBody>
            </Modal>
            <Modal isOpen={rejectModal} toggle={rejectToggle}>
                <ModalHeader className="pb-0">Reject Overtime Request</ModalHeader>
                <ModalBody className="pb-0 d-flex flex-column justify-content-center align-items-center ">
                    <Row>
                        <i
                            className="fa fa-times-circle fa-3x fa-primary"
                            style={{
                                color: '#f5365c',
                            }}
                            aria-hidden="true"
                        ></i>
                    </Row>
                    <Row className="mt-4">
                        Are you sure about Reject Overtime Request?
                    </Row>
                    <Button
                        color="danger"
                        onClick={() => handleApproveReject('rejected')}
                        className="w-100 mt-4"
                    >
                        Yes, Reject
                    </Button>
                    <Button
                        color="secondary"
                        onClick={rejectToggle}
                        className="w-100 mt-4 mb-4"
                    >
                        Cancel
                    </Button>
                </ModalBody>
            </Modal>
            <ToastContainer />
            <Footer />
        </>
    );
};

export default OvertimeRequests;
